import { ReactNode, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';
import { Loader2 } from 'lucide-react';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRoles?: string[];
}

export function ProtectedRoute({ children, requiredRoles }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, hasHydrated, checkAuth } = useAuthStore();
  const [isVerifying, setIsVerifying] = useState(false);
  const [hasAttemptedCheck, setHasAttemptedCheck] = useState(false);

  useEffect(() => {
    const hydrated = typeof hasHydrated === 'boolean' ? hasHydrated : true;
    if (hydrated && !isLoading && !isAuthenticated && !isVerifying && !hasAttemptedCheck) {
      setIsVerifying(true);
      setHasAttemptedCheck(true);
      Promise.resolve(checkAuth())
        .catch(() => {
          // swallow errors – the store will update isAuthenticated/isLoading states
        })
        .finally(() => setIsVerifying(false));
    }
  }, [checkAuth, hasHydrated, isLoading, isAuthenticated, isVerifying, hasAttemptedCheck]);

  useEffect(() => {
    if (isAuthenticated) {
      setHasAttemptedCheck(false);
    }
  }, [isAuthenticated]);

  const hydrationComplete = typeof hasHydrated === 'boolean' ? hasHydrated : true;
  const shouldShowLoader = isLoading || !hydrationComplete || isVerifying;

  // If we're still loading the initial auth check, show a loader
  if (shouldShowLoader) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
        <div role="status" aria-live="polite" data-testid="protected-route-loading">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
        <p className="mt-4 text-muted-foreground">Loading...</p>
      </div>
    );
  }

  // If not authenticated, redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // If roles are required, check user role
  if (requiredRoles && requiredRoles.length > 0) {
    const { user } = useAuthStore.getState();
    if (!user?.role || !requiredRoles.includes(user.role)) {
      return <Navigate to="/" replace />;
    }
  }

  // User is authenticated (and authorized if roles required), render children
  return <>{children}</>;
}
